import { useState, useEffect } from "react";
import { Toaster } from "react-hot-toast";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import LandingPage from "./components/dashboard/LandingPage.jsx";
import Callback from "./pages/callback.tsx";
import FigmaOAuthCallback from "./pages/FigmaOAuthCallback.tsx";
import OAuthPopupCallback from "./pages/OAuthPopupCallback.tsx";
import HomeDashboardV2 from "./components/dashboard/HomeDashboardV2.tsx";
import AgentDetailsPage from "./pages/AgentDetailsPage.tsx";
import CreateAgentPage from "./pages/CreateAgentPage.tsx";
import SpaceDetailsPage from "./pages/SpaceDetailsPage.tsx";
import CommunityHubPage from "./pages/CommunityPage.tsx";
import AIAssistantPage from "./components/dashboard/AIAssistantPage.tsx";
import SchedulePageV2 from "./components/dashboard/SchedulePageV2.tsx";
import DependencyAnalyzer from "./pages/tools/dev-utils/dependency-analyzer.tsx";
import Tickets from "./pages/tools/tickets.tsx";
import Layout from "./components/Layout.tsx";
import UserProfile from "./pages/settings/user-profile.tsx";
import MenuCustomization from "./pages/settings/menu-customization.tsx";
import UuidGenerator from "./pages/tools/dev-utils/uuid-generator.tsx";
import CodePlayground from "./pages/tools/dev-utils/code-playground.jsx";
import JsonFormatter from "./pages/tools/dev-utils/json-formatter.tsx";
import Base64Encoder from "./pages/tools/dev-utils/base64-encoder.tsx";
import TimestampConverter from "./pages/tools/dev-utils/timestamp-converter.tsx";
import RegexTester from "./pages/tools/dev-utils/regex-tester.tsx";
import ApiTester from "./pages/tools/dev-utils/api-tester.tsx";
import TaskList from "./pages/tools/dev-utils/task-list.tsx";
import ApiTest from "./pages/api-test.tsx";
import SprintRetroboard from "./pages/tools/project-management/sprint-retroboard.tsx";
import PersonalProjects from "./pages/tools/project-management/personal-projects.tsx";
import PersonalKanban from "./pages/tools/project-management/kanban-board.tsx";
import PMAgentPage from "./pages/tools/project-management/pm-agent.tsx";
import TimeBlocking from "./pages/tools/project-management/time-blocking.tsx";
import WeeklyPlanner from "./pages/tools/project-management/weekly-planner.tsx";
import IdeaCapture from "./pages/tools/project-management/idea-capture.tsx";
import PriorityMatrix from "./pages/tools/project-management/priority-matrix.tsx";
import ProjectTemplates from "./pages/tools/project-management/project-templates.tsx";
import FocusTimer from "./pages/tools/project-management/focus-timer.tsx";
import ReviewJournal from "./pages/tools/project-management/review-journal.tsx";
import GitHelper from "./pages/tools/dev-utils/git-helper.tsx";
import JwtTokenDecoder from "./pages/tools/dev-utils/jwt-token-decoder.tsx";
import NetworkAnalyzer from "./pages/tools/dev-utils/network-analyzer.tsx";
import TimerHub from "./pages/tools/timer-hub.tsx";
import ConsolidationTest from "./pages/tools/consolidation-test.tsx";
import FloatingChatButton from "./components/FloatingChatButton.tsx";
import LoginPromptModal from "./components/LoginPromptModal.tsx";
import MissionControl from "./components/missionControl/missionControl.jsx";
import TestAgentsAPI from "./pages/test-agents-api.tsx";
import AIConsole from "./components/AIConfig/AiConsole.jsx";
import Agents from "./components/agentsWorkflows/Agents.jsx";
import Workflows from "./components/agentsWorkflows/Workflows.jsx";
import ToolsLibrary from "./components/agentsWorkflows/Skills.tsx";
import VaultPage from "./pages/VaultPage.tsx";
import SkillsRegistryPage from "./pages/SkillsRegistryPage.tsx";
import ConnectionsPage from "./pages/ConnectionsPage.tsx";
import SkillsManagement from "./pages/SkillsManagement.tsx";
import AgentBuilder from "./pages/AgentBuilder.tsx";
import { decodeJWTToken, getCurrentUserProfile, type UserProfile as AuthUserProfile } from "./utils/auth.ts";
import OnboardingModal from "./components/modals/OnboardingModal.jsx";
import ApiKeyGateModal from "./components/modals/ApiKeyGateModal.tsx";
import { onboardingAPI } from "./services/api.js";
import { setStoredFeatures, clearStoredFeatures } from "./hooks/useFeature.tsx";
import DomainWorkflowPage from './pages/DomainWorkflowPage.tsx';
import IntegrationsPage from './pages/IntegrationsPage.jsx';
import HarnessConsole from './pages/HarnessConsole.tsx';
import MCPServersPage from './pages/MCPServersPage.jsx';
import { RunProvider } from './context/RunContext.tsx';
import MissionControlPage from './pages/MissionControlPage.jsx';
import LyranProfilePage from './pages/community/LyranProfilePage.jsx';
import SpacesHub from './pages/community/Community.jsx';
import ConversationsPage from './pages/ConversationsPage.jsx';

function LegacyCommunityProfileRedirect() {
  const { userId } = useParams();
  return <Navigate to={`/teams/${encodeURIComponent(userId || '')}`} replace />;
}

// Protected Route wrapper component
function ProtectedRoute({ children, isAuthenticated }: { children: React.ReactNode, isAuthenticated: boolean }) {
  const [showLoginModal, setShowLoginModal] = useState(false);

  console.log('ProtectedRoute - isAuthenticated:', isAuthenticated, 'path:', window.location.pathname); // Debug log

  useEffect(() => {
    if (!isAuthenticated) {
      console.log('Not authenticated, showing login modal for:', window.location.pathname);
      setShowLoginModal(true);
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <>
        {showLoginModal && (
          <LoginPromptModal onClose={() => {
            setShowLoginModal(false);
            // Navigate back to dashboard or home after closing modal
            window.location.href = '/dashboard';
          }} />
        )}
        {/* Show a placeholder or blank page while modal is displayed */}
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-gray-400 dark:text-gray-600">Loading...</div>
        </div>
      </>
    );
  }

  return <>{children}</>;
}

// Public Route wrapper for developer tools (no authentication required)
function PublicRoute({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

// Auth Route wrapper component (redirects to dashboard if already authenticated)
function AuthRoute({ children, isAuthenticated }: { children: React.ReactNode, isAuthenticated: boolean }) {
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

// Component to conditionally render FloatingChatButton
function ConditionalFloatingChatButton({ isAuthenticated }: { isAuthenticated: boolean }) {
  const location = useLocation();

  // Hide FloatingChatButton on landing, login, auth callback pages, and chat screen
  const shouldHideChat = location.pathname === '/' ||
    location.pathname === '/login' ||
    location.pathname.startsWith('/auth/') ||
    location.pathname === '/chat' ||
    location.pathname === '/chats' ||
    !isAuthenticated;

  if (shouldHideChat) {
    return null;
  }

  return <FloatingChatButton />;
}

const ONBOARDING_DONE_KEY = 'lyra_onboarding_done';

// Check server once; use localStorage to avoid rechecking on every load.
// Call this right after a token lands in localStorage.
async function checkAndShowOnboarding(show: (v: boolean) => void) {
  if (localStorage.getItem(ONBOARDING_DONE_KEY)) return; // already done on this browser
  try {
    const { onboarding } = await onboardingAPI.get();
    if (onboarding?.completedAt) {
      // Completed on another device — cache it so we skip the API next time
      localStorage.setItem(ONBOARDING_DONE_KEY, '1');
      if (onboarding.enabledFeatures?.length) setStoredFeatures(onboarding.enabledFeatures);
      return;
    }
    show(true); // new user or not yet completed
  } catch (e) { /* network error — skip silently */ }
}

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<AuthUserProfile | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    // Check for OAuth success tokens in URL first - but only if we're NOT on a callback route
    const isCallbackRoute = window.location.pathname.includes('/auth/') && window.location.pathname.includes('/callback');

    if (!isCallbackRoute) {
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      const googleLogin = urlParams.get('google_login');
      const linkedinLogin = urlParams.get('linkedin_login');

      if (tokenFromUrl && (googleLogin === 'success' || linkedinLogin === 'success')) {
        console.log('OAuth success detected, processing token...');

        // Decode the token to extract user profile
        const profile = decodeJWTToken(tokenFromUrl);
        console.log("JJJ", profile);
        if (profile) {
          console.log('User profile extracted:', {
            name: profile.name,
            email: profile.email,
            id: profile.id
          });

          localStorage.setItem('authToken', tokenFromUrl);
          setUserProfile(profile);
          setIsAuthenticated(true);
          setIsLoading(false);
          checkAndShowOnboarding(setShowOnboarding);

          // Clean up URL by removing query parameters
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
          return;
        } else {
          console.error('Failed to decode token');
        }
      }
    }

    // Check if user is already logged in
    const existingProfile = getCurrentUserProfile();
    console.log('Auth check - Profile found:', !!existingProfile);

    if (existingProfile) {
      setUserProfile(existingProfile);
      setIsAuthenticated(true);
      checkAndShowOnboarding(setShowOnboarding); // existing session: check once, cached after
    }
    setIsLoading(false);
  }, []);

  const handleLogin = (token: string) => {
    const profile = decodeJWTToken(token);
    if (profile) {
      localStorage.setItem('authToken', token);
      setUserProfile(profile);
      setIsAuthenticated(true);
      checkAndShowOnboarding(setShowOnboarding); // new login: check server
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem(ONBOARDING_DONE_KEY); // clear so next user on same browser gets checked
    clearStoredFeatures();
    setUserProfile(null);
    setIsAuthenticated(false);
    setShowOnboarding(false);
    // Hard navigate rather than relying on ProtectedRoute's reactive
    // login-prompt-modal fallback (meant for "session expired while
    // browsing", not an explicit sign-out) — and because /dashboard and
    // /ai-assistant are PublicRoute-wrapped (no auth-based redirect at
    // all), so flipping isAuthenticated alone never actually moves the
    // user off those pages. window.location also guarantees no stale
    // in-memory state survives the sign-out, same pattern already used by
    // LoginPromptModal's own onClose in this file.
    window.location.href = '/';
  };

  const handleOnboardingComplete = async () => {
    localStorage.setItem(ONBOARDING_DONE_KEY, '1'); // never show again on this browser
    try {
      const { onboarding } = await onboardingAPI.get();
      if (onboarding?.enabledFeatures?.length) setStoredFeatures(onboarding.enabledFeatures);
    } catch (e) { /* ignore */ }
    setShowOnboarding(false);
  };

  const handleOnboardingSkip = () => {
    localStorage.setItem(ONBOARDING_DONE_KEY, '1'); // never show again on this browser
    onboardingAPI.save({ role: 'other', useCase: 'automate', experience: 'beginner', tools: [], enabledFeatures: [] })
      .catch(() => {});
    setShowOnboarding(false);
  };

  return (
    <RunProvider>
    <div>
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />

      <Router>
        {isLoading ? (
          <div className="min-h-screen flex items-center justify-center bg-white">
            <div className="text-lg text-gray-600">Loading...</div>
          </div>
        ) : (
          <>
            <Routes>
              {/* Root / Landing — shows LandingPage for guests, redirects to dashboard if authenticated */}
              <Route
                path="/"
                element={
                  isAuthenticated
                    ? <Navigate to="/dashboard" replace />
                    : <LandingPage onLogin={handleLogin} />
                }
              />

              {/* /login is an alias for / (backward-compat with old links) */}
              <Route
                path="/login"
                element={
                  isAuthenticated
                    ? <Navigate to="/dashboard" replace />
                    : <LandingPage onLogin={handleLogin} />
                }
              />

              {/* Legacy workspace URLs deliberately converge on the explicit
                  integration boundary. Backend capability code remains in place
                  until its external consumers have migrated. */}
              <Route path="/repos" element={<Navigate to="/integrations#lyra-atlas" replace />} />
              <Route path="/engineering" element={<Navigate to="/integrations#lyra-atlas" replace />} />

              <Route
                path="/domains/:domainId"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <DomainWorkflowPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* OAuth callback routes */}
              <Route path="/auth/callback" element={<Callback onLogin={handleLogin} />} />
              <Route path="/auth/google/callback" element={<Callback onLogin={handleLogin} />} />
              <Route path="/auth/linkedin/callback" element={<Callback onLogin={handleLogin} />} />
              <Route path="/oauth/figma/callback" element={<FigmaOAuthCallback />} />
              <Route path="/oauth/popup-callback" element={<OAuthPopupCallback />} />

              {/* Debug route */}
              <Route path="/api-test" element={<ApiTest />} />

              {/* Dashboard - Public Access */}
              <Route
                path="/dashboard"
                element={
                  <PublicRoute>
                    <HomeDashboardV2 onLogout={handleLogout} />
                  </PublicRoute>
                }
              />
              <Route
                path="/ai-assistant"
                element={
                  <PublicRoute>
                    <AIAssistantPage onLogout={handleLogout} />
                  </PublicRoute>
                }
              />
              <Route
                path="/schedule"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <SchedulePageV2 onLogout={handleLogout} />
                  </ProtectedRoute>
                }
              />
              {/* <Route
                path="/agent-studio"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <AgentStudio />
                    </Layout>
                  </ProtectedRoute>
                }
              /> */}
              <Route
                path="/agents"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <Agents />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agents/create"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <CreateAgentPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agents/:agentId"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <AgentDetailsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/workflows"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <Workflows />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/skills/registry"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <SkillsRegistryPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/connections"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <ConnectionsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vault"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <VaultPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools-library"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <ToolsLibrary />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/skills/manage"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <SkillsManagement />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agent-builder/:agentId"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <AgentBuilder />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/flow-builder"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <MissionControl />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mission-control"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <MissionControlPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teams/:userId"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <LyranProfilePage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teams"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <SpacesHub />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="/community/:userId" element={<LegacyCommunityProfileRedirect />} />
              <Route path="/community" element={<Navigate to="/teams" replace />} />
              <Route
                path="/spaces/:spaceId"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <SpaceDetailsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/marketplace"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <CommunityHubPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/conversations"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <ConversationsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/chats"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <ConversationsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              {/* Developer Tools - Public Access (No Authentication Required) */}
              <Route
                path="/tools/dependency-analyzer"
                element={
                  <PublicRoute>
                    <Layout onLogout={handleLogout}>
                      <DependencyAnalyzer />
                    </Layout>
                  </PublicRoute>
                }
              />
              <Route
                path="/tools/uuid-generator"
                element={
                  <PublicRoute>
                    <Layout onLogout={handleLogout}>
                      <UuidGenerator />
                    </Layout>
                  </PublicRoute>
                }
              />
              <Route
                path="/tools/code-playground"
                element={
                  <PublicRoute>
                    <Layout onLogout={handleLogout}>
                      <CodePlayground />
                    </Layout>
                  </PublicRoute>
                }
              />
              <Route
                path="/tools/json-formatter"
                element={
                  <PublicRoute>
                    <Layout onLogout={handleLogout}>
                      <JsonFormatter />
                    </Layout>
                  </PublicRoute>
                }
              />
              <Route
                path="/tools/regex-tester"
                element={
                  <PublicRoute>
                    <Layout onLogout={handleLogout}>
                      <RegexTester />
                    </Layout>
                  </PublicRoute>
                }
              />
              <Route
                path="/tools/api-tester"
                element={
                  <PublicRoute>
                    <Layout onLogout={handleLogout}>
                      <ApiTester />
                    </Layout>
                  </PublicRoute>
                }
              />
              <Route
                path="/tools/task-list"
                element={
                  <PublicRoute>
                    <Layout onLogout={handleLogout}>
                      <TaskList />
                    </Layout>
                  </PublicRoute>
                }
              />
              <Route
                path="/tools/project-management/sprint-retroboard"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <SprintRetroboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/git-helper"
                element={
                  <PublicRoute>
                    <Layout onLogout={handleLogout}>
                      <GitHelper />
                    </Layout>
                  </PublicRoute>
                }
              />
              <Route
                path="/tools/jwt-token-decoder"
                element={
                  <PublicRoute>
                    <Layout onLogout={handleLogout}>
                      <JwtTokenDecoder />
                    </Layout>
                  </PublicRoute>
                }
              />
              <Route
                path="/tools/network-analyzer"
                element={
                  <PublicRoute>
                    <Layout onLogout={handleLogout}>
                      <NetworkAnalyzer />
                    </Layout>
                  </PublicRoute>
                }
              />
              <Route
                path="/tools/base64-encoder"
                element={
                  <PublicRoute>
                    <Layout onLogout={handleLogout}>
                      <Base64Encoder />
                    </Layout>
                  </PublicRoute>
                }
              />
              <Route
                path="/tools/timestamp-converter"
                element={
                  <PublicRoute>
                    <Layout onLogout={handleLogout}>
                      <TimestampConverter />
                    </Layout>
                  </PublicRoute>
                }
              />
              <Route
                path="/tools/tickets"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <Tickets />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Project Management Routes */}
              <Route
                path="/tools/project-management/personal-projects"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <PersonalProjects />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/project-management/personal-kanban"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <PersonalKanban />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/project-management/project-agent"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <PMAgentPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/project-management/pm-agent"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Navigate to="/tools/project-management/project-agent" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/project-management/time-blocking"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <TimeBlocking />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/project-management/weekly-planner"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <WeeklyPlanner />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/project-management/idea-capture"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <IdeaCapture />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/project-management/priority-matrix"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <PriorityMatrix />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/project-management/project-templates"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <ProjectTemplates />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/project-management/focus-timer"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <FocusTimer />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="/test-agents-api" element={<TestAgentsAPI />} />
              <Route
                path="/tools/review-journal"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <ReviewJournal />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Hub Components */}
              <Route
                path="/tools/timer-hub"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <TimerHub />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/consolidation-test"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <ConsolidationTest />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Catch-all route for tools that don't have specific pages yet */}
              <Route
                path="/tools/*"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <div className="p-8">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tool Coming Soon</h1>
                        <p className="text-gray-600 dark:text-gray-400 mt-2">
                          This tool is currently under development and will be available soon.
                        </p>
                        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                          <p className="text-sm text-blue-700 dark:text-blue-300">
                            In the meantime, you can explore other available tools from the sidebar menu.
                          </p>
                        </div>
                      </div>
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/finance/*"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <div className="p-8">
                        <h1 className="text-2xl font-bold">Finance Section</h1>
                        <p>Finance features coming soon!</p>
                      </div>
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <div className="p-8">
                        <h1 className="text-2xl font-bold">Settings</h1>
                        <p>Settings page coming soon!</p>
                      </div>
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-console"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <AIConsole />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="/harness" element={<ProtectedRoute isAuthenticated={isAuthenticated}><Layout onLogout={handleLogout}><HarnessConsole /></Layout></ProtectedRoute>} />
              <Route
                path="/integrations"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <IntegrationsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mcp-servers"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <MCPServersPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/lyra-api-key"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <MCPServersPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/profile"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <UserProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings/menu-customization"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Layout onLogout={handleLogout}>
                      <MenuCustomization />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Fallback routes */}
              <Route
                path="*"
                element={
                  <div className="min-h-screen flex items-center justify-center bg-gray-100">
                    <div className="text-center">
                      <h1 className="text-2xl font-bold text-gray-800 mb-4">Page Not Found</h1>
                      <p className="text-gray-600 mb-4">The page you're looking for doesn't exist.</p>
                      <p className="text-sm text-gray-500 mb-4">
                        Current path: {window.location.pathname}
                      </p>
                      <p className="text-sm text-gray-500 mb-4">
                        Authenticated: {isAuthenticated ? 'Yes' : 'No'}
                      </p>
                      <button
                        onClick={() => window.location.href = isAuthenticated ? '/dashboard' : '/login'}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Go {isAuthenticated ? 'Home' : 'to Login'}
                      </button>
                    </div>
                  </div>
                }
              />
            </Routes>
            {/* <ConditionalFloatingChatButton isAuthenticated={isAuthenticated} /> */}
            {showOnboarding && (
              <OnboardingModal
                onComplete={handleOnboardingComplete}
                onSkip={handleOnboardingSkip}
              />
            )}
            {isAuthenticated && <ApiKeyGateModal />}
          </>
        )}
      </Router>
    </div>
    </RunProvider>
  );
}
